// src/pages/EBookPage.jsx
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import Navbar from "../components/Navbar";
import EBookForm from "../components/EBookForm";
import Footer from "../components/Footer";
import PromptRibbon from "../components/Ribbon";
import coverImg from "../assets/images/ebook-cover.png";

/* ===== API base ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Minimal HTTP helper */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

const feedbackApi = {
  create: (payload) => http(`/api/feedbacks`, { method: "POST", data: payload }),
};

/* ===== Layout Shell ===== */
const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  background: #fff;
`;

const Main = styled.main`
  flex: 1 0 auto;
  padding-top: clamp(60px, 7vw, 80px);
`;

/* ===== Hero / Header ===== */
const HeaderSection = styled.section`
  position: relative;
  overflow: clip;
  padding: clamp(2.25rem, 5vw, 4rem) 1rem clamp(2rem, 4vw, 3rem);
  background: radial-gradient(
    1000px 600px at 10% -10%,
    #4e89aa 0%,
    #2a5a7a 45%,
    #244d66 70%,
    #1e3e52 100%
  );
  color: #fff;
  isolation: isolate;

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: auto;
    border-radius: 50%;
    filter: blur(48px);
    opacity: 0.25;
    pointer-events: none;
    transform: translateZ(0);
  }
  &::before {
    width: 520px;
    height: 520px;
    left: -160px;
    top: -180px;
    background: radial-gradient(
      circle at 30% 30%,
      #7fd1ff,
      #3dccc7 60%,
      transparent 70%
    );
  }
  &::after {
    width: 580px;
    height: 580px;
    right: -220px;
    bottom: -220px;
    background: radial-gradient(
      circle at 70% 70%,
      #ffb489,
      #ff7f50 55%,
      transparent 70%
    );
  }
`;

const HeaderContent = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: clamp(1rem, 4vw, 2.5rem);
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  padding: clamp(1rem, 3.5vw, 1.5rem);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.25);
  backdrop-filter: saturate(135%) blur(10px);
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: clamp(0.75rem, 1.8vw, 0.85rem);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #c7e7f7;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
`;

const PageTitle = styled.h1`
  margin: 0.6rem 0 0.4rem;
  font-weight: 800;
  line-height: 1.08;
  font-size: clamp(1.6rem, 5vw, 2.4rem);
`;

const PageSubtitle = styled.p`
  margin: 0.25rem 0 1rem;
  opacity: 0.95;
  font-size: clamp(0.95rem, 2.2vw, 1.05rem);
  color: #e8f6ff;
`;

const HeroCTAs = styled.div`
  display: inline-flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.2rem;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

/* ===== Unified Button (identical for both) ===== */
const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 0.85rem 1.15rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1;
  letter-spacing: 0.2px;
  text-decoration: none;
  cursor: pointer;
  user-select: none;
  border: 0;
  font-family: inherit;
  transform: translateZ(0);
  transition: transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease;

  &:hover {
    transform: translateY(-2px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CTAButton = styled.button`
  ${buttonBase};
  color: #fff;
  background: linear-gradient(45deg, #ff914d, #ff6b35);
  box-shadow: 0 8px 24px rgba(255, 107, 53, 0.35);

  &:hover {
    box-shadow: 0 12px 28px rgba(255, 107, 53, 0.45);
  }
`;

/* Stats */
const StatRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.9rem;
  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const StatPill = styled.div`
  padding: 0.5rem 0.7rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  font-size: 0.85rem;
  color: #e8f6ff;
`;

const VisualCard = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 14px 48px rgba(0, 0, 0, 0.35);
  background: linear-gradient(135deg, #3dccc7 0%, #2a5a7a 100%);
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

/* ===== Benefits ===== */
const BenefitsSection = styled.section`
  background: #f7f8fa;
  padding: clamp(1.5rem, 4vw, 2.5rem) 1rem;
`;
const BenefitsContent = styled.div` max-width: 1100px; margin: 0 auto; `;
const BenefitsTitle = styled.h2`
  text-align: center; color: #1f2937; font-weight: 800;
  font-size: clamp(1.3rem, 4vw, 1.9rem); margin: 0 0 clamp(1rem, 3vw, 1.5rem);
`;
const BenefitsGrid = styled.div`
  display: grid; grid-template-columns: repeat(12, 1fr);
  gap: clamp(0.8rem, 2.5vw, 1.25rem);
`;
const BenefitCard = styled.div`
  grid-column: span 4; background: #ffffff; border-radius: 14px;
  padding: clamp(0.9rem, 2.5vw, 1.2rem); border: 1px solid #eef2f6;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease;
  &:hover{ transform: translateY(-4px); box-shadow: 0 12px 26px rgba(15, 23, 42, 0.09); }
  @media (max-width: 900px){ grid-column: span 6; }
  @media (max-width: 560px){ grid-column: span 12; }
`;
const BenefitIcon = styled.div` font-size: clamp(1.6rem, 4.5vw, 2rem); line-height: 1; margin-bottom: 0.45rem; `;
const BenefitTitle = styled.h3` color: #111827; font-weight: 700; font-size: clamp(1rem, 3vw, 1.15rem); margin: 0 0 0.3rem; `;
const BenefitDescription = styled.p` color: #4b5563; line-height: 1.55; font-size: clamp(0.9rem, 2.4vw, 0.95rem); margin: 0; `;

/* ===== Anchor target for CTA ===== */
const FormAnchor = styled.div` scroll-margin-top: clamp(60px, 7vw, 80px); `;

/* ===== Toast (success message after closing modal) ===== */
const ToastWrap = styled.div`
  position: fixed;
  top: calc(clamp(60px, 7vw, 80px) + 12px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1200;
  padding: 0 8px;
`;
const Toast = styled.div`
  background: #0f1b2d;
  color: #e6f3ff;
  border: 1px solid #1e293b;
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.25);
  font-weight: 600;
  font-size: 0.95rem;
`;

/* ===== Modal elements ===== */
const ModalWrap = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 16px;
  overflow: auto;
  overscroll-behavior: contain;
`;
const ModalCard = styled.div`
  width: min(780px, 100%);
  max-height: 92vh;
  background: #0f1b2d; 
  color: #f1f1f1;
  border: 1px solid #1e293b; 
  border-radius: 16px;
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;

  @media (max-width: 480px) {
    border-radius: 12px;
  }
`;
const ModalHeader = styled.div`
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #1e293b;

  h3 {
    margin: 0;
    font-size: clamp(1rem, 3.5vw, 1.1rem);
    font-weight: 800;
  }
`;
const CloseBtn = styled.button`
  ${buttonBase};
  padding: 0.65rem 0.95rem;
  background: #0c1626;
  color: #f1f1f1;
  border: 1px solid #213247;
  box-shadow: none;
  &:hover { background: #0e1b2f; }
`;
const ModalBody = styled.form`
  display: grid;
  grid-template-rows: 1fr auto;
  min-height: 0;
`;
const ScrollArea = styled.div`
  padding: 14px 16px;
  overflow: auto;
  min-height: 0;
`;
const Actions = styled.div`
  display: flex; 
  gap: 10px; 
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid #1e293b;
  background: #0f1b2d;

  @media (max-width: 520px) {
    flex-wrap: wrap;
    justify-content: stretch;
    & > * { flex: 1 1 100%; }
  }
`;
const SubmitBtn = styled(CTAButton).attrs({ type: "submit" })`
  background: #3dccc7;
  box-shadow: none;
  &:hover { background: #2a5a7a; }
`;
const Grid = styled.div` display: grid; gap: 12px; `;
const Field = styled.label` display: grid; gap: 6px; font-size: 14px; `;
const Input = styled.input`
  padding: 10px 12px; border-radius: 10px; border: 1px solid #1e293b;
  background: #0c1626; color: #f1f1f1;
`;
const Textarea = styled.textarea`
  min-height: 120px; padding: 12px; border-radius: 12px; border: 1px solid #1e293b;
  background: #0c1626; color: #f1f1f1; resize: vertical;
`;

const EBookPage = () => {
  const [openFeedback, setOpenFeedback] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [toast, setToast] = useState(""); // <— NEW: toast message after submit

  const [form, setForm] = useState({
    name: "",
    email: "",
    valuable: "",
    confused: "",
    confidence: "",
  });

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(id);
  }, [toast]);

  // Lock background scroll when modal is open
  useEffect(() => {
    if (!openFeedback) return;
    const { body } = document;
    const prev = body.style.overflow;
    body.style.overflow = "hidden";
    return () => { body.style.overflow = prev; };
  }, [openFeedback]);

  // Close modal on ESC
  useEffect(() => {
    if (!openFeedback) return;
    const onKey = (e) => { if (e.key === "Escape") setOpenFeedback(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openFeedback]);

  const scrollToForm = () => {
    const el = document.getElementById("ebook-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  async function handleSubmit(e){
    e.preventDefault();
    setMsg("");
    if (!form.name.trim() || !form.email.trim()) {
      setMsg("Name and Email are required.");
      return;
    }
    try {
      setSaving(true);
      await feedbackApi.create({
        name: form.name.trim(),
        email: form.email.trim(),
        valuable: form.valuable.trim(),
        confused: form.confused.trim(),
        confidence: form.confidence.trim(),
      });
      // Success: clear form, close modal, and show toast
      setForm({ name:"", email:"", valuable:"", confused:"", confidence:"" });
      setOpenFeedback(false);
      setToast("Your feedback has been submitted. Thank you!");
      setMsg("");
    } catch (e) {
      setMsg(e.message || "Failed to submit feedback.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Navbar />
      <PageShell>
        {/* Toast */}
        {toast && (
          <ToastWrap role="status" aria-live="polite">
            <Toast>{toast}</Toast>
          </ToastWrap>
        )}

        <Main>
          {/* Hero */}
          <HeaderSection>
            <HeaderContent>
              <HeroCard>
                <Eyebrow>Free eBook • Construction + AI</Eyebrow>
                <PageTitle>Build Smarter with AI</PageTitle>
                <PageSubtitle>
                  Learn how managers, estimators, and engineers save time, cut
                  costs, and improve workflows using practical, real-world AI.
                </PageSubtitle>

                <HeroCTAs>
                  <CTAButton onClick={scrollToForm}>Get the eBook</CTAButton>
                  <CTAButton onClick={() => setOpenFeedback(true)}>Give Feedback</CTAButton>
                </HeroCTAs>

                <StatRow>
                  <StatPill>⚡ Save up to 50% time</StatPill>
                  <StatPill>🎯 95%+ accuracy workflows</StatPill>
                  {/* <StatPill>🏗️ 10K+ projects influenced</StatPill> */}
                </StatRow>
              </HeroCard>

              <VisualCard aria-hidden="true">
                <img src={coverImg} alt="AI in construction" />
              </VisualCard>
            </HeaderContent>
          </HeaderSection>

          {/* Benefits */}
          <BenefitsSection id="learn">
            <BenefitsContent>
              <BenefitsTitle>What You’ll Learn</BenefitsTitle>
              <BenefitsGrid>
                <BenefitCard>
                  <BenefitIcon>🤖</BenefitIcon>
                  <BenefitTitle>AI Fundamentals</BenefitTitle>
                  <BenefitDescription>
                    Understand core AI concepts and where they deliver value in construction.
                  </BenefitDescription>
                </BenefitCard>
                <BenefitCard>
                  <BenefitIcon>📊</BenefitIcon>
                  <BenefitTitle>Data Analytics</BenefitTitle>
                  <BenefitDescription>
                    Use data for planning, forecasting risk, and making confident decisions.
                  </BenefitDescription>
                </BenefitCard>
                <BenefitCard>
                  <BenefitIcon>⚡</BenefitIcon>
                  <BenefitTitle>Automation Tools</BenefitTitle>
                  <BenefitDescription>
                    Streamline estimating, scheduling, and reporting with automation.
                  </BenefitDescription>
                </BenefitCard>
                <BenefitCard>
                  <BenefitIcon>🎯</BenefitIcon>
                  <BenefitTitle>Project Optimization</BenefitTitle>
                  <BenefitDescription>
                    Optimize timelines, resources, and quality with simple frameworks.
                  </BenefitDescription>
                </BenefitCard>
                <BenefitCard>
                  <BenefitIcon>💰</BenefitIcon>
                  <BenefitTitle>Cost Management</BenefitTitle>
                  <BenefitDescription>
                    Apply AI-powered methods for accurate estimates and budget control.
                  </BenefitDescription>
                </BenefitCard>
                <BenefitCard>
                  <BenefitIcon>🔮</BenefitIcon>
                  <BenefitTitle>Future Trends</BenefitTitle>
                  <BenefitDescription>
                    Stay ahead with the latest tools and workflows shaping the industry.
                  </BenefitDescription>
                </BenefitCard>
              </BenefitsGrid>
            </BenefitsContent>
          </BenefitsSection>

          {/* Form */}
          <FormAnchor id="ebook-form" />
          <EBookForm />
        </Main>

        <Footer />
        <PromptRibbon />
      </PageShell>

      {/* Feedback Modal */}
      {openFeedback && (
        <ModalWrap onClick={(e)=>{ if(e.target===e.currentTarget) setOpenFeedback(false); }}>
          <ModalCard role="dialog" aria-modal="true" aria-labelledby="fb-title">
            <ModalHeader>
              <h3 id="fb-title">Share your feedback</h3>
              <CloseBtn onClick={()=>setOpenFeedback(false)} aria-label="Close feedback form">Close</CloseBtn>
            </ModalHeader>

            <ModalBody onSubmit={handleSubmit}>
              <ScrollArea>
                <Grid>
                  <Field>
                    <span>What is your Name? *</span>
                    <Input
                      value={form.name}
                      onChange={e=>setForm(f=>({...f, name:e.target.value}))}
                      placeholder="Your name"
                      required
                    />
                  </Field>
                  <Field>
                    <span>Enter your email. *</span>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e=>setForm(f=>({...f, email:e.target.value}))}
                      placeholder="you@company.com"
                      required
                    />
                  </Field>
                  <Field>
                    <span>What parts of the eBook were most valuable or insightful to you — and why?</span>
                    <Textarea
                      value={form.valuable}
                      onChange={e=>setForm(f=>({...f, valuable:e.target.value}))}
                      placeholder="Your thoughts…"
                    />
                  </Field>
                  <Field>
                    <span>Where did you feel confused, bored, or wanted more clarity or examples?</span>
                    <Textarea
                      value={form.confused}
                      onChange={e=>setForm(f=>({...f, confused:e.target.value}))}
                      placeholder="Tell us what to improve…"
                    />
                  </Field>
                  <Field>
                    <span>After reading, do you feel more confident about applying AI to your work in construction? Why or why not?</span>
                    <Textarea
                      value={form.confidence}
                      onChange={e=>setForm(f=>({...f, confidence:e.target.value}))}
                      placeholder="Be honest—this helps us a lot!"
                    />
                  </Field>
                  {msg && <div style={{color:"#ffb4a6"}}>{msg}</div>}
                </Grid>
              </ScrollArea>

              <Actions>
                <CloseBtn type="button" onClick={()=>setOpenFeedback(false)}>Cancel</CloseBtn>
                <SubmitBtn disabled={saving}>
                  {saving ? "Submitting…" : "Submit Feedback"}
                </SubmitBtn>
              </Actions>
            </ModalBody>
          </ModalCard>
        </ModalWrap>
      )}
    </>
  );
};

export default EBookPage;
